import {
  GENERATE_RECONCILIATION_REQUEST,
  GENERATE_RECONCILIATION_PAGE_SUCCESS,
  GENERATE_RECONCILIATION_COMPLETE,
  GENERATE_RECONCILIATION_FAILURE,
  GENERATE_RECONCILIATION_IN_PROGRESS,
  GENERATE_RECONCILIATION_RETRY_AFTER,
  QUERY_RECONCILIATION_REQUEST,
  QUERY_RECONCILIATION_SUCCESS,
  QUERY_RECONCILIATION_FAILURE,
  LOAD_RECONCILIATION_HEADER_REQUEST,
  LOAD_RECONCILIATION_HEADER_SUCCESS,
  LOAD_RECONCILIATION_HEADER_FAILURE,
  LOAD_RECONCILIATION_TRANSACTIONS_REQUEST,
  LOAD_RECONCILIATION_TRANSACTIONS_SUCCESS,
  LOAD_RECONCILIATION_TRANSACTIONS_FAILURE,
  VIEW_RECONCILIATION_REPORT_REQUEST,
  VIEW_RECONCILIATION_REPORT_PAGE_SUCCESS,
  VIEW_RECONCILIATION_REPORT_COMPLETE,
  VIEW_RECONCILIATION_REPORT_FAILURE,
  FETCH_RECONCILIATION_FILTERS_REQUEST,
  FETCH_RECONCILIATION_FILTERS_SUCCESS,
  FETCH_RECONCILIATION_FILTERS_FAILURE,
  FETCH_RECONCILIATION_LOGS_REQUEST,
  FETCH_RECONCILIATION_LOGS_SUCCESS,
  FETCH_RECONCILIATION_LOGS_FAILURE,
  SAVE_RECONCILIATION_REPORT_REQUEST,
  SAVE_RECONCILIATION_REPORT_SUCCESS,
  SAVE_RECONCILIATION_REPORT_FAILURE,
  RESET_RECONCILIATION,
  UPDATE_ORGANISATION_TAX_BALANCE_REQUEST,
  UPDATE_ORGANISATION_TAX_BALANCE_SUCCESS,
  UPDATE_ORGANISATION_TAX_BALANCE_FAILURE,
} from './constants';
import {
  GenerateReconciliationPageSuccessAction,
  GenerateReconciliationRequestAction,
  GenerateReconciliationRetryAfterAction,
  LoadReconciliationHeaderSuccessAction,
  LoadReconciliationTransactionsSuccessAction,
  QueryReconciliationSuccessAction,
  ViewReconciliationReportCompleteAction,
  ViewReconciliationReportPageSuccessAction,
  ViewReconciliationReportRequestAction,
  FetchReconciliationFiltersSuccessAction,
  FetchReconciliationLogsSuccessAction,
  ReconciliationAction,
  ReconciliationState,
  SaveReconciliationReportFailureAction,
  FetchReconciliationLogsFailureAction,
  UpdateOrganisationTaxBalanceFailureAction,
} from './types';

/** No view payload loaded — also used whenever a new view fetch is started. */
const VIEW_RECONCILIATION_DATA_DEFAULT = undefined;

const initialState: ReconciliationState = {
  loading: false,
  error: undefined,
  errorCode: undefined,
  reportId: undefined,

  generating: false,
  generationComplete: false,
  accrualsLastId: undefined,
  cashAccountingLastId: undefined,
  transactionsSize: 0,
  exceptionsSize: 0,
  othersSize: 0,
  summarizedSize: 0,
  totalTax: undefined,
  totalTaxTransactions: undefined,
  totalTaxExceptions: undefined,
  totalTaxOthers: undefined,
  dataStatus: undefined,
  retryAfterSeconds: undefined,

  headerLoading: false,
  headerData: undefined,

  queryLoading: false,
  journals: [],
  journalsByType: {},

  filtersLoading: false,
  mappingCodes: undefined,
  accounts: undefined,
  taxRates: undefined,
  logsLoading: false,
  logs: [],
  logsPage: 1,
  logsPageSize: 10,
  logsTotalElements: 0,
  logsTotalPages: 0,

  saveReportLoading: false,
  saveReportSuccess: false,

  taxBalanceUpdating: false,

  viewReconciliationData: VIEW_RECONCILIATION_DATA_DEFAULT,
};

/** Published reconciliation log table — not part of draft generate state; keep when resetting. */
function carryReconciliationLogsSlice(
  from: ReconciliationState
): Pick<
  ReconciliationState,
  | 'logs'
  | 'logsPage'
  | 'logsPageSize'
  | 'logsTotalElements'
  | 'logsTotalPages'
  | 'logsLoading'
> {
  return {
    logs: from.logs,
    logsPage: from.logsPage,
    logsPageSize: from.logsPageSize,
    logsTotalElements: from.logsTotalElements,
    logsTotalPages: from.logsTotalPages,
    logsLoading: from.logsLoading,
  };
}

export const reconciliationReducer = (
  state = initialState,
  action: ReconciliationAction
): ReconciliationState => {
  switch (action.type) {
    // Generate
    case GENERATE_RECONCILIATION_REQUEST:
      return handleGenerateRequest(state, action);
    case GENERATE_RECONCILIATION_PAGE_SUCCESS:
      return handleGeneratePageSuccess(state, action);
    case GENERATE_RECONCILIATION_COMPLETE:
      return handleGenerateComplete(state);
    case GENERATE_RECONCILIATION_FAILURE:
      return handleGenerateFailure(state);
    case GENERATE_RECONCILIATION_IN_PROGRESS:
      return handleGenerateInProgress(state);
    case GENERATE_RECONCILIATION_RETRY_AFTER:
      return handleGenerateRetryAfter(state, action);

    // Query
    case QUERY_RECONCILIATION_REQUEST:
      return handleQueryRequest(state);
    case QUERY_RECONCILIATION_SUCCESS:
      return handleQuerySuccess(state, action);
    case QUERY_RECONCILIATION_FAILURE:
      return handleQueryFailure(state);

    // Load header
    case LOAD_RECONCILIATION_HEADER_REQUEST:
      return handleLoadHeaderRequest(state);
    case LOAD_RECONCILIATION_HEADER_SUCCESS:
      return handleLoadHeaderSuccess(state, action);
    case LOAD_RECONCILIATION_HEADER_FAILURE:
      return handleLoadHeaderFailure(state);

    // Load transactions
    case LOAD_RECONCILIATION_TRANSACTIONS_REQUEST:
      return handleLoadTransactionsRequest(state);
    case LOAD_RECONCILIATION_TRANSACTIONS_SUCCESS:
      return handleLoadTransactionsSuccess(state, action);
    case LOAD_RECONCILIATION_TRANSACTIONS_FAILURE:
      return handleLoadTransactionsFailure(state);

    // View saved report
    case VIEW_RECONCILIATION_REPORT_REQUEST:
      return handleViewReportRequest(state, action);
    case VIEW_RECONCILIATION_REPORT_PAGE_SUCCESS:
      return handleViewReportPageSuccess(state, action);
    case VIEW_RECONCILIATION_REPORT_COMPLETE:
      return handleViewReportComplete(state, action as ViewReconciliationReportCompleteAction);
    case VIEW_RECONCILIATION_REPORT_FAILURE:
      return handleViewReportFailure(state);

    // Filters
    case FETCH_RECONCILIATION_FILTERS_REQUEST:
      return handleFetchFiltersRequest(state);
    case FETCH_RECONCILIATION_FILTERS_SUCCESS:
      return handleFetchFiltersSuccess(state, action);
    case FETCH_RECONCILIATION_FILTERS_FAILURE:
      return handleFetchFiltersFailure(state);

    // Logs
    case FETCH_RECONCILIATION_LOGS_REQUEST:
      return handleFetchLogsRequest(state);
    case FETCH_RECONCILIATION_LOGS_SUCCESS:
      return handleFetchLogsSuccess(state, action);
    case FETCH_RECONCILIATION_LOGS_FAILURE:
      return handleFetchLogsFailure(state, action);

    // Save report
    case SAVE_RECONCILIATION_REPORT_REQUEST:
      return handleSaveReportRequest(state);
    case SAVE_RECONCILIATION_REPORT_SUCCESS:
      return handleSaveReportSuccess(state);
    case SAVE_RECONCILIATION_REPORT_FAILURE:
      return handleSaveReportFailure(state, action);

    // Update organisation tax balance b/f
    case UPDATE_ORGANISATION_TAX_BALANCE_REQUEST:
      return handleUpdateTaxBalanceRequest(state);
    case UPDATE_ORGANISATION_TAX_BALANCE_SUCCESS:
      return handleUpdateTaxBalanceSuccess(state);
    case UPDATE_ORGANISATION_TAX_BALANCE_FAILURE:
      return handleUpdateTaxBalanceFailure(state, action);

    // Reset
    case RESET_RECONCILIATION:
      return {
        ...initialState,
        ...carryReconciliationLogsSlice(state),
        logsLoading: false,
      };

    default:
      return state;
  }
};

// ── Generate ───────────────────────────────────────────────────────────────

function handleGenerateRequest(
  _state: ReconciliationState,
  action: GenerateReconciliationRequestAction
): ReconciliationState {
  return {
    ...initialState,
    ...carryReconciliationLogsSlice(_state),
    loading: true,
    generating: true,
    generationComplete: false,
    reportId: action.payload.reportId,
    dataStatus: undefined,
    retryAfterSeconds: undefined,
    error: undefined,
  };
}

function handleGeneratePageSuccess(
  state: ReconciliationState,
  action: GenerateReconciliationPageSuccessAction
): ReconciliationState {
  const d = action.payload;
  const totalTaxOthers = Number(
    (d as any).totalTaxOthers ?? (d as any).totalTaxOther ?? 0
  );
  return {
    ...state,
    accrualsLastId: d.accrualsLastId,
    cashAccountingLastId: d.cashAccountingLastId,
    transactionsSize: d.transactionsSize,
    exceptionsSize: d.exceptionsSize,
    othersSize: d.othersSize ?? 0,
    summarizedSize: d.summarizedSize,
    totalTaxTransactions: Number(d.totalTaxTransactions ?? 0),
    totalTaxExceptions: Number(d.totalTaxExceptions ?? 0),
    totalTaxOthers,
    totalTax:
      Number(d.totalTaxTransactions ?? 0) +
      Number(d.totalTaxExceptions ?? 0) +
      totalTaxOthers,
    dataStatus: d.dataStatus,
  };
}

function handleGenerateComplete(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    loading: false,
    generating: false,
    generationComplete: true,
    dataStatus: undefined,
    retryAfterSeconds: undefined,
  };
}

function handleGenerateFailure(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    loading: false,
    generating: false,
    generationComplete: false,
    dataStatus: undefined,
  };
}

function handleGenerateInProgress(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    dataStatus: 'IN_PROGRESS',
  };
}

function handleGenerateRetryAfter(
  state: ReconciliationState,
  action: GenerateReconciliationRetryAfterAction
): ReconciliationState {
  return {
    ...state,
    dataStatus: 'RETRY_AFTER',
    retryAfterSeconds: action.payload.retryAfterSeconds,
  };
}

// ── Query ──────────────────────────────────────────────────────────────────

function handleQueryRequest(state: ReconciliationState): ReconciliationState {
  return {
    ...state,
    queryLoading: true,
  };
}

function handleQuerySuccess(
  state: ReconciliationState,
  action: QueryReconciliationSuccessAction
): ReconciliationState {
  const d = action.payload;
  const transactionType = d.transactionType;
  const journals = d.journals || [];
  return {
    ...state,
    queryLoading: false,
    journals,
    journalsByType: {
      ...state.journalsByType,
      [transactionType]: journals,
    },
  };
}

function handleQueryFailure(state: ReconciliationState): ReconciliationState {
  return {
    ...state,
    queryLoading: false,
  };
}

// ── Load header ────────────────────────────────────────────────────────────

function handleLoadHeaderRequest(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    headerLoading: true,
  };
}

function handleLoadHeaderSuccess(
  state: ReconciliationState,
  action: LoadReconciliationHeaderSuccessAction
): ReconciliationState {
  return {
    ...state,
    headerLoading: false,
    headerData: action.payload.headerData,
  };
}

function handleLoadHeaderFailure(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    headerLoading: false,
    headerData: undefined,
  };
}

// ── Load transactions ───────────────────────────────────────────────────────

function handleLoadTransactionsRequest(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    loading: true,
    generating: true,
    generationComplete: false,
    dataStatus: undefined,
    error: undefined,
  };
}

function handleLoadTransactionsSuccess(
  state: ReconciliationState,
  action: LoadReconciliationTransactionsSuccessAction
): ReconciliationState {
  const d = action.payload;
  const totalTaxOthers = Number(
    (d as any).totalTaxOthers ?? (d as any).totalTaxOther ?? 0
  );

  return {
    ...state,
    loading: false,
    generating: false,
    generationComplete: true,
    accrualsLastId: d.accrualsLastId,
    cashAccountingLastId: d.cashAccountingLastId,
    transactionsSize: d.transactionsSize,
    exceptionsSize: d.exceptionsSize,
    othersSize: d.othersSize ?? 0,
    summarizedSize: d.summarizedSize,
    totalTaxTransactions: Number(d.totalTaxTransactions ?? 0),
    totalTaxExceptions: Number(d.totalTaxExceptions ?? 0),
    totalTaxOthers,
    totalTax:
      Number(d.totalTaxTransactions ?? 0) +
      Number(d.totalTaxExceptions ?? 0) +
      totalTaxOthers,
    dataStatus: d.dataStatus,
    retryAfterSeconds: undefined,
  };
}

function handleLoadTransactionsFailure(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    loading: false,
    generating: false,
    generationComplete: false,
    dataStatus: undefined,
    retryAfterSeconds: undefined,
  };
}

// ── View saved report ──────────────────────────────────────────────────────

/**
 * Every new view-reconciliation fetch starts from this baseline so stale
 * `viewReconciliationData` never survives across requests.
 */
function viewReconciliationFetchRequestBase(): Pick<
  ReconciliationState,
  | 'loading'
  | 'generating'
  | 'generationComplete'
  | 'viewReconciliationData'
  | 'dataStatus'
  | 'error'
> {
  return {
    loading: true,
    generating: true,
    generationComplete: false,
    viewReconciliationData: VIEW_RECONCILIATION_DATA_DEFAULT,
    dataStatus: undefined,
    error: undefined,
  };
}

function handleViewReportRequest(
  state: ReconciliationState,
  _action: ViewReconciliationReportRequestAction
): ReconciliationState {
  return {
    ...state,
    ...viewReconciliationFetchRequestBase(),
    reportId: undefined,
  };
}

function handleViewReportPageSuccess(
  state: ReconciliationState,
  action: ViewReconciliationReportPageSuccessAction
): ReconciliationState {
  return {
    ...state,
    viewReconciliationData: action.payload,
  };
}

function handleViewReportComplete(
  state: ReconciliationState,
  action: ViewReconciliationReportCompleteAction
): ReconciliationState {
  return {
    ...state,
    loading: false,
    generating: false,
    generationComplete: true,
    dataStatus: undefined,
    reportId: action.payload?.reportId,
  };
}

function handleViewReportFailure(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    loading: false,
    generating: false,
    generationComplete: false,
    viewReconciliationData: VIEW_RECONCILIATION_DATA_DEFAULT,
    reportId: undefined,
  };
}

// ── Filters ────────────────────────────────────────────────────────────────

function handleFetchFiltersRequest(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    filtersLoading: true,
  };
}

function handleFetchFiltersSuccess(
  state: ReconciliationState,
  action: FetchReconciliationFiltersSuccessAction
): ReconciliationState {
  return {
    ...state,
    filtersLoading: false,
    mappingCodes: action.payload.mappingCodes,
    accounts: action.payload.accounts,
    taxRates: action.payload.taxRates,
  };
}

function handleFetchFiltersFailure(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    filtersLoading: false,
  };
}

// ── Logs ───────────────────────────────────────────────────────────────────

function handleFetchLogsRequest(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    logsLoading: true,
    error: undefined,
  };
}

function handleFetchLogsSuccess(
  state: ReconciliationState,
  action: FetchReconciliationLogsSuccessAction
): ReconciliationState {
  return {
    ...state,
    logsLoading: false,
    logs: action.payload.logs || [],
    logsPage: action.payload.page ?? 1,
    logsPageSize: action.payload.pageSize ?? state.logsPageSize,
    logsTotalElements: action.payload.totalElements ?? 0,
    logsTotalPages: action.payload.totalPages ?? 0,
    error: undefined,
  };
}

function handleFetchLogsFailure(
  state: ReconciliationState,
  action: FetchReconciliationLogsFailureAction
): ReconciliationState {
  return {
    ...state,
    logsLoading: false,
    logs: [],
    logsTotalElements: 0,
    logsTotalPages: 0,
    error: action.payload?.error,
  };
}

// ── Save report ─────────────────────────────────────────────────────────────

function handleSaveReportRequest(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    saveReportLoading: true,
    saveReportSuccess: false,
    error: undefined,
  };
}

function handleSaveReportSuccess(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    saveReportLoading: false,
    saveReportSuccess: true,
    error: undefined,
  };
}

function handleSaveReportFailure(
  state: ReconciliationState,
  action: SaveReconciliationReportFailureAction
): ReconciliationState {
  return {
    ...state,
    saveReportLoading: false,
    saveReportSuccess: false,
    error: action.payload?.error,
  };
}

// ── Update organisation tax balance b/f ─────────────────────────────────────

function handleUpdateTaxBalanceRequest(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    taxBalanceUpdating: true,
    error: undefined,
    errorCode: undefined,
  };
}

function handleUpdateTaxBalanceSuccess(
  state: ReconciliationState
): ReconciliationState {
  return {
    ...state,
    taxBalanceUpdating: false,
    error: undefined,
    errorCode: undefined,
  };
}

function handleUpdateTaxBalanceFailure(
  state: ReconciliationState,
  action: UpdateOrganisationTaxBalanceFailureAction
): ReconciliationState {
  return {
    ...state,
    taxBalanceUpdating: false,
    error: action.payload?.error,
    errorCode: undefined,
  };
}
